import { useInboundInvoices } from '@/use/invoices/inbound'
import { useOutboundInvoices } from '@/use/invoices/outbound'
import { capitalize, cloneDeep } from 'lodash'
import { AutocompletePayload, AutocompleteResponse } from '@/types/interfaces/api-v2/autocomplete'
import { ref } from 'vue'
import { FilterResources } from '@/types/enums/filter-types'
import { CompanyRoleTypes, InvoiceCalculatedStatus } from '@/types/enums'
import { useTextMapping } from '@/use/text-mappings'
import { useSession } from '@/use/session'
import { VENDOR } from '@/constants/permissions'
import { NULL } from '@/constants/ransack'
import { containsAllSearch } from '@/utils/search'

const { inboundInvoicesAutoComplete } = useInboundInvoices()
const { invoicesAutoComplete, statuses } = useOutboundInvoices()

const isOutbound = ref(false)

// const invoiceStatuses: Record <string, string> = {
//   Draft: 'draft',
//   Sent: 'sent',
//   Received: 'sent',
//   Paid: 'paid',
//   'Partially Paid': 'partially_paid',
//   Void: 'void',
//   Approved: 'approved',
//   Rejected: 'rejected',
//   'Approved To Send': 'approved_to_send'
// }

const AGREEMENT_CLIENT_INVOICE_FREQUENCY = [
  { id: 'monthly', text: 'Monthly', value: '0' },
  { id: 'quarterly', text: 'Quarterly', value: '1' },
  { id: 'semi_annually', text: 'Semi Annually', value: '2' },
  { id: 'annually', text: 'Annually', value: '3' },
  { id: 'three_yearly', text: 'Three Yearly', value: '4' },
  { id: 'five_yearly', text: 'Five Yearly', value: '5' },
  { id: 'one_time', text: 'One Time', value: '6' },
  { id: 'custom', text: 'Custom', value: '7' }
]

interface CalculatedStatus {
  code: number;
  text: string;
}

interface InvoiceAutocompletePayload extends AutocompletePayload {
  finderParams?: {
    includeGrouped?: boolean,
    objectScope?: string
  }
}

interface StatusTypes {
  standards: CalculatedStatus[]
  updatedWordings: CalculatedStatus[]
}
const invoiceStatuses: {
  [CompanyRoleTypes.CLIENT]: StatusTypes,
  [CompanyRoleTypes.VENDOR]: StatusTypes,
  both: StatusTypes
} = {
  client: { standards: [], updatedWordings: [] },
  vendor: { standards: [], updatedWordings: [] },
  both: { standards: [], updatedWordings: [] }
}

const isOutboundForStatus = (): boolean => {
  const { session } = useSession()

  return session.currentCompanyType === VENDOR
}

const getInvoiceStatuses = async (as: CompanyRoleTypes | 'both', inbound: boolean) => {
  const typeToConsider = isOutboundForStatus() ? 'updatedWordings' : 'standards'
  if (invoiceStatuses[as][typeToConsider].length) return invoiceStatuses[as][typeToConsider]

  const res = await statuses(as, inbound)
  if (res.data) {
    const statuses = cloneDeep(res.data)
    const { mapText } = useTextMapping()
    if (typeToConsider === 'updatedWordings') {
      statuses.forEach((status: CalculatedStatus) => {
        status.text = [
          InvoiceCalculatedStatus.APPROVED, InvoiceCalculatedStatus.REJECTED
        ].includes(status.code)
          ? capitalize(mapText(status.text))
          : status.text
      })
    }
    invoiceStatuses[as][typeToConsider] = statuses

    return invoiceStatuses[as][typeToConsider]
  } else {
    return []
  }
}

const invoiceAutoCompleteFetchFn = async (params: InvoiceAutocompletePayload) => {
  if (params.finderParams) {
    params.finderParams.includeGrouped = true
    params.finderParams.objectScope = 'both'
  } else {
    params.finderParams = { includeGrouped: true, objectScope: 'both' }
  }

  const { data, nextPage } = (isOutbound.value)
    ? await invoicesAutoComplete(params) as { data: AutocompleteResponse[], nextPage: number }
    : await inboundInvoicesAutoComplete(params) as { data: AutocompleteResponse[], nextPage: number }
  return {
    data: data.map((item: AutocompleteResponse, index: number) => {
      return {
        id: `${index}-${item.text}`,
        text: item.text,
        value: item.text
      }
    }),
    nextPage
  }
}

const INVOICE_NUMBER = (outbound: boolean, config = {}, params = { field: 'invoice_number' }, fetchFn: any = invoiceAutoCompleteFetchFn) => {
  isOutbound.value = outbound
  return {
    text: 'Number',
    canSelect: true,
    popoverProps: {
      textKey: 'text',
      height: 'unset',
      slotKey: 'invoice-number-filter',
      headerText: 'Invoice Number',
      size: 'lg',
      contentType: 'string',
      rowType: 'text',
      showHeaders: true,
      fetchFn,
      params,
      searchKeys: ['invoice_number'],
      tokenSeparators: ['\t', '\n', ',']
    },
    pinned: false,
    isPrimary: false,
    resource: FilterResources.INVOICE,
    ...config
  }
}

const INVOICE_CUSTOM_NUMBER = (outbound: boolean, config = {}, params = { field: 'custom_invoice_number' }, fetchFn: any = invoiceAutoCompleteFetchFn) => {
  isOutbound.value = outbound
  return {
    text: 'Custom Number',
    canSelect: true,
    popoverProps: {
      textKey: 'text',
      height: 'unset',
      slotKey: 'invoice-custom-number-filter',
      headerText: 'Invoice Custom Number',
      size: 'lg',
      contentType: 'string',
      rowType: 'text',
      showHeaders: true,
      fetchFn,
      params,
      searchKeys: ['custom_invoice_number'],
      tokenSeparators: ['\t', '\n', ',']
    },
    pinned: false,
    isPrimary: false,
    resource: FilterResources.INVOICE,
    ...config
  }
}

const INVOICE_STATUS = (as: CompanyRoleTypes | 'both', config = {}, inbound: boolean) => {
  return {
    text: 'Status',
    canSelect: true,
    popoverProps: {
      textKey: 'text',
      height: 'unset',
      slotKey: 'invoice-status-filter',
      headerText: 'Invoice Status',
      size: 'lg',
      contentType: 'string',
      rowType: 'text',
      showHeaders: true,
      fetchFn: async ({ terms }: { terms?: string[] }) => {
        const res = await getInvoiceStatuses(as, inbound)
        const data = containsAllSearch(cloneDeep(res), terms).map((calculatedStaus) => {
          return {
            id: calculatedStaus.code,
            text: calculatedStaus.text,
            value: calculatedStaus.code
          }
        })

        return {
          data,
          totalCount: data.length,
          totalPages: 1
        }
      },
      params: { q: { s: 'invoice_status asc' } },
      searchKeys: ['text']
    },
    pinned: false,
    isPrimary: false,
    resource: FilterResources.INVOICE,
    ...config
  }
}

const INVOICE_DATE_OF_ISSUE = (outbound: boolean, config = {}) => {
  return {
    text: 'Date of Issue',
    canSelect: true,
    popoverProps: {
      textKey: 'text',
      height: 'unset',
      slotKey: 'invoice-date-of-issue-filter',
      headerText: 'Invoice Date of Issue',
      size: 'md',
      contentType: 'date',
      rowType: 'text',
      showHeaders: true,
      withoutTimeZone: true
    },
    pinned: false,
    isPrimary: false,
    resource: FilterResources.INVOICE,
    ...config
  }
}

const INVOICE_DUE_DATE = (outbound: boolean, config = {}) => {
  return {
    text: 'Due Date',
    canSelect: true,
    popoverProps: {
      textKey: 'text',
      height: 'unset',
      slotKey: 'invoice-due-date-filter',
      headerText: 'Invoice Due Date',
      size: 'md',
      contentType: 'date',
      rowType: 'text',
      showHeaders: true,
      withoutTimeZone: true
    },
    pinned: false,
    isPrimary: false,
    resource: FilterResources.INVOICE,
    ...config
  }
}

const INVOICE_RECEIVED_DATE = (config = {}) => {
  return {
    text: 'Received Date',
    canSelect: true,
    popoverProps: {
      textKey: 'text',
      height: 'unset',
      slotKey: 'invoice-received-date-filter',
      headerText: 'Invoice Received Date',
      size: 'md',
      contentType: 'date',
      rowType: 'text',
      showHeaders: true
    },
    pinned: false,
    isPrimary: false,
    resource: FilterResources.INVOICE,
    ...config
  }
}

const INVOICE_AGREEMENT_CONTRACT_NUMBER = (outbound: boolean, config = {}, params = { field: 'agreement_number' }, fetchFn: any = invoiceAutoCompleteFetchFn) => {
  isOutbound.value = outbound
  return {
    text: 'Scheduled Service Agreement Number',
    canSelect: true,
    popoverProps: {
      textKey: 'text',
      height: 'unset',
      slotKey: 'invoice-agreement-number-filter',
      headerText: 'Scheduled Service Agreement Number',
      size: 'lg',
      contentType: 'string',
      rowType: 'text',
      showHeaders: true,
      fetchFn,
      params,
      searchKeys: ['agreement_number'],
      collectionPredicate: 'eq'
    },
    pinned: false,
    isPrimary: false,
    resource: FilterResources.INVOICE,
    ...config
  }
}

const INVOICE_AGREEMENT_CUSTOM_NUMBER = (outbound: boolean, config = {}, params = { field: 'agreement_custom_no' }, fetchFn: any = invoiceAutoCompleteFetchFn) => {
  isOutbound.value = outbound
  return {
    text: 'Schedule Service Custom ID',
    canSelect: true,
    popoverProps: {
      textKey: 'text',
      height: 'unset',
      slotKey: 'invoice-schedule-service-custom-id-filter',
      headerText: 'Schedule Service Custom ID',
      size: 'lg',
      contentType: 'string',
      rowType: 'text',
      showHeaders: true,
      fetchFn,
      params,
      searchKeys: ['agreement_custom_no']
    },
    pinned: false,
    isPrimary: false,
    resource: FilterResources.INVOICE,
    ...config
  }
}

const INVOICE_EXTERNAL_REGION = (outbound: boolean, config = {}, params = { field: 'inv_region' }, fetchFn: any = invoiceAutoCompleteFetchFn) => {
  isOutbound.value = outbound
  return {
    text: 'Invoice External Region',
    canSelect: true,
    popoverProps: {
      textKey: 'text',
      height: 'unset',
      slotKey: 'invoice-external-region-filter',
      headerText: 'Invoice External Region',
      size: 'lg',
      contentType: 'string',
      rowType: 'text',
      showHeaders: true,
      fetchFn,
      params,
      searchKeys: ['inv_region']
    },
    pinned: false,
    isPrimary: false,
    resource: FilterResources.INVOICE,
    ...config
  }
}

const INVOICE_BACKGROUND_TASK = (outbound: boolean, config = {}, params = { field: 'background_task' }, fetchFn: any = invoiceAutoCompleteFetchFn) => {
  isOutbound.value = outbound
  return {
    text: 'Background Task',
    canSelect: true,
    popoverProps: {
      textKey: 'text',
      height: 'unset',
      slotKey: 'background-task-filter',
      headerText: 'Background Task',
      size: 'lg',
      contentType: 'string',
      rowType: 'text',
      showHeaders: true,
      fetchFn,
      params,
      searchKeys: ['background_task'],
      collectionPredicate: 'eq'
    },
    pinned: false,
    isPrimary: false,
    resource: FilterResources.INVOICE,
    ...config
  }
}

const INVOICE_AGREEMENT_CONTRACT_INVOICE_FREQUENCY = (outbound: boolean, config = {}) => {
  isOutbound.value = outbound
  return {
    text: 'Scheduled Service Agreement Invoice Frequency',
    canSelect: true,
    popoverProps: {
      textKey: 'text',
      height: 'unset',
      slotKey: 'invoice-agreement-invoice-frequency-filter',
      headerText: 'Scheduled Service Agreement Invoice Frequency',
      size: 'lg',
      contentType: 'string',
      rowType: 'text',
      showHeaders: true,
      fetchFn: () => {
        const occurrences = AGREEMENT_CLIENT_INVOICE_FREQUENCY.filter((frequency) => frequency.text)
        return {
          data: occurrences,
          totalCount: occurrences.length,
          totalPages: 1
        }
      },
      filterUnique: true,
      params: { q: { s: 'id asc' } },
      searchKeys: [],
      operator: [{ text: 'Is', value: '_in' }]
    },
    pinned: false,
    isPrimary: false,
    resource: FilterResources.INVOICE,
    ...config
  }
}

const INVOICE_AGREEMENT_CONTRACT = (outbound: boolean, config = {}) => {
  const options = [
    { label: 'Is', value: 'true', data: ['1'], predicate: 'not_null' },
    { label: 'Is Not', value: 'false', data: ['0'], predicate: 'not_null' },
    { label: 'Is Empty', value: NULL, data: [NULL] }
  ]
  isOutbound.value = outbound
  return {
    text: 'Scheduled Service Agreement',
    canSelect: true,
    popoverProps: {
      textKey: 'text',
      height: 'unset',
      slotKey: 'agreement-contract-filter',
      headerText: 'Scheduled Service Agreement',
      size: 'md',
      contentType: 'select',
      rowType: 'text',
      showHeaders: true,
      fetchFn: () => {
        return {
          data: options,
          totalCount: options.length,
          totalPages: 1
        }
      },
      searchKeys: [],
      operator: [{ text: 'Is', value: '_not_null' }]
    },
    pinned: false,
    isPrimary: false,
    resource: FilterResources.INVOICE,
    ...config
  }
}

const INVOICE_ACCOUNTING_SYNC_DATE = (outbound: boolean, config = {}) => {
  return {
    text: 'Accounting Sync Date',
    canSelect: true,
    popoverProps: {
      textKey: 'text',
      height: 'unset',
      slotKey: 'invoice-accounting-sync-date-filter',
      headerText: 'Invoice Accounting Sync Date',
      size: 'md',
      contentType: 'date',
      rowType: 'text',
      showHeaders: true
    },
    pinned: false,
    isPrimary: false,
    resource: FilterResources.INVOICE,
    ransackableScope: true,
    ...config
  }
}

export {
  INVOICE_NUMBER,
  INVOICE_CUSTOM_NUMBER,
  INVOICE_STATUS,
  INVOICE_DATE_OF_ISSUE,
  INVOICE_DUE_DATE,
  INVOICE_RECEIVED_DATE,
  INVOICE_AGREEMENT_CONTRACT_NUMBER,
  INVOICE_AGREEMENT_CUSTOM_NUMBER,
  INVOICE_EXTERNAL_REGION,
  INVOICE_BACKGROUND_TASK,
  INVOICE_AGREEMENT_CONTRACT_INVOICE_FREQUENCY,
  INVOICE_AGREEMENT_CONTRACT,
  INVOICE_ACCOUNTING_SYNC_DATE
}
